import { I18nThemed } from 'interfaces/mixins';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { w } from '@dojo/framework/core/vdom';
import { DNode } from '@dojo/framework/core/interfaces';
import { Store } from '@dojo/framework/stores/Store';
import { storeInject } from '@dojo/framework/stores/StoreInjector';
import { Bundle, Messages } from '@dojo/framework/i18n/i18n';
import { LocalizedMessages } from '@dojo/framework/core/mixins/I18n';

import { HorizontalBarChart, ChartProperties, targetPlugin } from '@holmescorp/charts';
import { SiteMeta } from 'lcc-state';

import css from '../css/progress-chart.m.css';

export interface ProgressChartProperties {
	ditabookBundle?: Bundle<Messages>;
	settingsBundle?: Bundle<Messages>;
	labels?: string[];
	datasets?: [];
	size?: { width: number; height: number };
	ariaLabel?: string;
	chartscales?: { [key: string]: any };
	legend?: boolean;
	onClick?: (event?: MouseEvent, activeElements?: Array<object>) => void;
	drawComplete?: () => void;
	events?: string[];
	useMobileVariant?: boolean;
}

const defaultScales = {
	xAxes: [
		{
			type: 'linear',
			ticks: { beginAtZero: true, min: 0, max: 100 },
			scaleLabel: { display: false, labelString: '%' }
		}
	]
};

const ditarootsMissing = `[
	"[settings] ditaroots"
	"[settings] ditaroots"
	"[settings] ditaroots"
	"[settings] ditaroots"
	"[settings] ditaroots"
	"[settings] ditaroots"
]`;

@theme(css)
@storeInject({
	name: 'lcc-state',
	paths: [['dynls']],
	getProperties: (store: Store<SiteMeta>) => {
		const { get, path } = store;
		const dynls = get(path('dynls'));
		return {
			ditabookBundle: dynls && dynls.get('ditabook'),
			settingsBundle: dynls && dynls.get('settings')
		};
	}
})
export class ProgressChart extends I18nThemed<ProgressChartProperties> {
	protected get i18nDitabook(): LocalizedMessages<Messages> {
		const { ditabookBundle } = this.properties;
		return ditabookBundle === undefined
			? { isPlaceholder: true, format: () => '', messages: {} as Messages }
			: this.localizeBundle(ditabookBundle);
	}

	protected get i18nSettings(): LocalizedMessages<Messages> {
		const { settingsBundle } = this.properties;
		return settingsBundle === undefined
			? { isPlaceholder: true, format: () => '', messages: {} as Messages }
			: this.localizeBundle(settingsBundle);
	}

	protected render(): DNode {
		const { ditaroots = ditarootsMissing } = this.i18nSettings.messages;
		const roots: string[] = JSON.parse(ditaroots);
		const {
			key,
			labels = roots,
			datasets = [],
			size,
			ariaLabel = 'progress',
			chartscales = {},
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			onClick = () => {},
			drawComplete,
			legend = true,
			events = ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
			useMobileVariant = false
		} = this.properties;

		const scales = Object.assign({}, defaultScales, chartscales);
		const isMobile = window.innerWidth < 992 && useMobileVariant;
		if (isMobile) {
			if (!scales.yAxes || scales.yAxes.length === 0) {
				scales.yAxes = [{ ticks: {}, mirror: false }];
			}
			const yAxis = scales.yAxes[0];
			yAxis.ticks = yAxis.ticks || {};
			yAxis.ticks.mirror = isMobile;
			yAxis.ticks.z = isMobile ? 1 : 0;
			yAxis.ticks.fontColor = '#333';
		}

		const chartprops: ChartProperties = {
			pluginCode: [targetPlugin],
			drawComplete,
			ariaLabel,
			size,
			legend: { display: legend, position: 'bottom' },
			options: {
				onClick,
				scales,
				events,
				plugins: [
					{
						targetarea: {
							fillColor: '#90ff9020',
							strokeColor: '#00ff00',
							target: 80,
							label: {
								enabled: true,
								text: 'TARGET',
								strokeColor: '#222222',
								fontSize: 9
							}
						}
					}
				]
			},
			data: {
				labels,
				datasets
			}
		};
		return w(HorizontalBarChart, { key, ...chartprops });
	}
}
